import React, { useState } from "react";
import { StudyMaterialFile } from "../../../../types/dataModel";
import { formatVideoDuration } from "../../../../common/Utils";
import CustomModal from "../../../../core/common/customModal";

export type VideoUploadMode = "single" | "multiple";

export interface VideoMaterialDraft {
  file: StudyMaterialFile;
  title: string;
}

interface Props {
  mode: VideoUploadMode;
  onModeChange: (mode: VideoUploadMode) => void;
  isEditing: boolean;
  singleTitle: string;
  onSingleTitleChange: (value: string) => void;
  singleTitleError?: string;
  files: StudyMaterialFile[];
  onFilesSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (file: StudyMaterialFile, index: number) => void;
  filesError?: string;
  multiVideoMaterials: VideoMaterialDraft[];
  multiTitleErrors: Record<number, string>;
  onMultiTitleChange: (index: number, value: string) => void;
  onMultiRemove: (index: number) => void;
  uploadProgress?: number;
  multiUploadProgress?: Record<number, number>;
  isUploading?: boolean;
  uploadedFiles?: Set<number>;
  existingThumbnailUrl?: string | null;
  onThumbnailFileChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveThumbnail?: () => void | Promise<void>;
}

const VideoMaterialUploadSection: React.FC<Props> = ({
  mode,
  onModeChange,
  isEditing,
  singleTitle,
  onSingleTitleChange,
  singleTitleError,
  files,
  onFilesSelected,
  onRemoveFile,
  filesError,
  multiVideoMaterials,
  multiTitleErrors,
  onMultiTitleChange,
  onMultiRemove,
  uploadProgress = 0,
  multiUploadProgress = {},
  isUploading = false,
  uploadedFiles = new Set<number>(),
  existingThumbnailUrl = null,
  onThumbnailFileChange,
  onRemoveThumbnail,
}) => {
  const acceptTypes = ".mp4,.avi,.mov,.mkv";
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState<string | null>(null);

  const handleThumbnailClick = (thumbnail: string) => {
    setSelectedThumbnail(thumbnail);
    setShowThumbnailModal(true);
  };

  const handleCloseModal = () => {
    setShowThumbnailModal(false);
    setSelectedThumbnail(null);
  };

  return (
    <div className="mb-3">
      {!isEditing && (
        <div className="d-flex align-items-center mb-3">
          <div className="btn-group btn-group-sm" role="group" aria-label="Video upload mode">
            <button
              type="button"
              className={`fs-12 btn ${mode === "single" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => onModeChange("single")}
            >
              Upload Single Material
            </button>
            <button
              type="button"
              className={`fs-12 btn ${mode === "multiple" ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => onModeChange("multiple")}
            >
              Upload Multiple Material
            </button>
          </div>
        </div>
      )}

      {mode === "single" && (
        <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            type="text"
            className={`form-control ${singleTitleError ? "is-invalid" : ""}`}
            placeholder="Enter material title..."
            value={singleTitle}
            onChange={(event) => onSingleTitleChange(event.target.value)}
          />
          {singleTitleError && <div className="invalid-feedback">{singleTitleError}</div>}
        </div>
      )}

      {/* Thumbnail selection for edit mode */}
      {isEditing && mode === "single" && (
        <div className="mt-3">
          <label className="form-label">Thumbnail</label>
          {existingThumbnailUrl ? (
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="flex-shrink-0">
                <img
                  src={existingThumbnailUrl}
                  alt="Video thumbnail"
                  className="rounded"
                  style={{ width: "128px", height: "72px", objectFit: "cover", cursor: "pointer" }}
                  onClick={() => handleThumbnailClick(existingThumbnailUrl)}
                />
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <div className="fs-12 text-muted mb-2">No thumbnail available. Select an image file to add one or select a video file to generate a thumbnail.</div>
            </div>
          )}
          {onThumbnailFileChange && (
            <div>
              <input
                type="file"
                className="form-control form-control-sm"
                accept="image/*"
                onChange={onThumbnailFileChange}
                disabled={isUploading}
              />
              <div className="form-text">Select an image file (JPG, PNG, etc.) for thumbnail</div>
            </div>
          )}
        </div>
      )}

      <div hidden={mode === "single" && files.length > 0}>
        <label className="form-label">{mode === "single" ? "Select Video File" : "Select Video Files"}</label>
        <input
          type="file"
          className={`form-control ${filesError ? "is-invalid" : ""}`}
          multiple={mode === "multiple"}
          onChange={onFilesSelected}
          accept={acceptTypes}
        />
      </div>
      {filesError && <div className="invalid-feedback">{filesError}</div>}
      <div className="form-text">Supported formats: MP4, AVI, MOV, MKV Videos</div>



      {mode === "single" && files.length > 0 && (
        <div className="mt-3">
          <h6 className="mb-2">Selected Files:</h6>
          <div className="d-flex flex-wrap gap-2">
            {files.map((file, index) => {
              const thumbnail = (file as any).thumbnail;
              return (
                <div
                  key={`${file.name || file.fileName}-${index}`}
                  className="d-flex flex-column bg-light p-2 rounded w-100"
                >
                  <div className="d-flex align-items-start justify-content-between gap-2 row">
                    <div className="d-flex align-items-center justify-content-between gap-2">
                    {thumbnail && (
                      <div className="flex-shrink-0">
                        <img
                          src={thumbnail}
                          alt="Video thumbnail"
                          className="rounded"
                          style={{ width: "128px", height: "72px", objectFit: "cover", cursor: "pointer" }}
                          onClick={() => handleThumbnailClick(thumbnail)}
                        />
                      </div>
                    )}
                      {!isUploading && (
                        <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => onRemoveFile(file, index)}>
                          <i className="ti ti-x" />
                        </button>
                      )}
                    </div>

                    <div className="flex-grow-1 d-flex flex-column">
                      <div className="fs-12 fw-medium">
                        <i className="ti ti-file" /> {file.name || file.fileName}
                      </div>
                      <div className="d-flex gap-2 align-items-center flex-wrap">
                        {file.size && (
                          <div className="fs-11 text-muted">Size: {(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        )}
                        {(file as any).videoDuration && (
                          <div className="fs-11 text-muted">
                            <i className="ti ti-clock" /> Duration: {formatVideoDuration((file as any).videoDuration)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {isUploading && index === 0 && uploadProgress > 0 && (
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fs-11 text-muted">Uploading...</span>
                        <span className="fs-11 fw-medium">{uploadProgress}%</span>
                      </div>
                      <div className="progress" style={{ height: "8px" }}>
                        <div
                          className="progress-bar bg-primary"
                          role="progressbar"
                          style={{ width: `${uploadProgress}%` }}
                          aria-valuenow={uploadProgress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mode === "multiple" && multiVideoMaterials.length > 0 && (
        <div className="mt-3">
          <h6 className="mb-2">Videos to Upload:</h6>
          <div className="d-flex flex-column gap-2">
            {multiVideoMaterials.map((material, index) => {
              const error = multiTitleErrors[index];
              const progress = multiUploadProgress[index] || 0;
              const isCurrentlyUploading = isUploading && progress > 0 && progress < 100;
              const isUploaded = uploadedFiles.has(index);
              const thumbnail = (material.file as any).thumbnail;
              return (
                <div key={`${material.file.name || material.file.fileName}-${index}`} className="bg-light p-3 rounded">
                  <div className="d-flex align-items-start justify-content-between gap-3 row">
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <label className="form-label mb-1">Title</label> 
                        {!isCurrentlyUploading && (
                          <button type="button" className="btn btn-sm btn-outline-danger flex-shrink-0" onClick={() => onMultiRemove(index)}>
                            <i className="ti ti-x" />
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
                        value={material.title}
                        onChange={(event) => onMultiTitleChange(index, event.target.value)}
                        disabled={isCurrentlyUploading}
                      />

                    {thumbnail && (
                      <div className="flex-shrink-0">
                        <img
                          src={thumbnail}
                          alt="Video thumbnail"
                          className="rounded"
                          style={{ width: "128px", height: "72px", objectFit: "cover", cursor: "pointer" }}
                          onClick={() => handleThumbnailClick(thumbnail)}
                        />
                      </div>
                    )}
                    <div className="flex-grow-1">
                      {/* <label className="form-label mb-1">Title</label>
                      <input
                        type="text"
                        className={`form-control form-control-sm ${error ? "is-invalid" : ""}`}
                        value={material.title}
                        onChange={(event) => onMultiTitleChange(index, event.target.value)}
                        disabled={isCurrentlyUploading}
                      /> */}
                      {error && <div className="invalid-feedback">{error}</div>}
                      <div className="fs-11 text-muted mt-1">
                        <i className="ti ti-file" /> {material.file.name || material.file.fileName}{" "}
                        {material.file.size && `• ${(material.file.size / 1024 / 1024).toFixed(2)} MB`}
                        {(material.file as any).videoDuration && (
                          <> • <i className="ti ti-clock" /> {formatVideoDuration((material.file as any).videoDuration)}</>
                        )}
                      </div>
                      {isCurrentlyUploading && (
                        <div className="mt-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="fs-11 text-muted">Uploading...</span>
                            <span className="fs-11 fw-medium">{progress}%</span>
                          </div>
                          <div className="progress" style={{ height: "8px" }}>
                            <div
                              className="progress-bar bg-primary"
                              role="progressbar"
                              style={{ width: `${progress}%` }}
                              aria-valuenow={progress}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                        </div>
                      )}
                      {isUploaded && !isCurrentlyUploading && (
                        <div className="mt-2">
                          <span className="fs-11 text-success">
                            <i className="ti ti-check" /> Uploaded successfully
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Thumbnail Preview Modal */}
      <CustomModal
        zindex={99999}  
        show={showThumbnailModal}
        onClose={handleCloseModal}
        title="Thumbnail Preview"
        size="lg"
        bodyContent={
          selectedThumbnail && (
            <div className="text-center">
              <img
                src={selectedThumbnail}
                alt="Video thumbnail preview"
                className="img-fluid rounded"
                style={{ maxHeight: "70vh", objectFit: "contain" }}
              />
            </div>
          )
        }
        footerContent={
          <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
            Close
          </button>
        }
      />
    </div>
  );
};

export default VideoMaterialUploadSection;

